// frontend/src/components/common/BoardingInfoModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, MapPin, ExternalLink, AlertCircle } from 'lucide-react';
import axios from '../../axiosConfig';
import { useTheme } from '../../contexts/ThemeContext';
import AppImage from './AppImage';

/**
 * BoardingInfoModal Component
 *
 * Displays boarding location information in a modal with:
 * - Multi-language text instructions
 * - Map/guide links
 * - Visual guides (images)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {number} props.waypointId - Waypoint ID to fetch boarding info for
 * @param {string} props.waypointName - Display name of the waypoint
 * @param {number} props.entityId - Entity ID for context
 */
const BoardingInfoModal = ({
  isOpen,
  onClose,
  waypointId,
  waypointName,
  entityId,
  initialBoardingInfo
}) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  const normalizeBoardingInfo = (info) => {
    if (!info) {
      return null;
    }

    const textValue =
      info.text ?? info.boarding_location_info ?? info.instructions ?? null;
    const urlValue =
      info.url ?? info.boarding_location_url ?? null;
    const imageValue =
      info.imageUrl ?? info.boarding_location_image_url ?? info.image ?? null;
    const hasInfo =
      info.hasInfo ?? Boolean(textValue || urlValue || imageValue);

    return {
      text: textValue,
      url: urlValue,
      imageUrl: imageValue,
      language: info.language || null,
      isTranslated: Boolean(info.isTranslated),
      hasInfo
    };
  };

  const initialInfo = useMemo(
    () => normalizeBoardingInfo(initialBoardingInfo),
    [initialBoardingInfo]
  );

  const [boardingInfo, setBoardingInfo] = useState(initialInfo);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setBoardingInfo(initialInfo);
      setError(null);
    }
  }, [initialInfo, isOpen]);

  // Fetch boarding info when modal opens
  useEffect(() => {
    if (isOpen && waypointId) {
      fetchBoardingInfo();
    }
  }, [isOpen, waypointId]);

  const fetchBoardingInfo = async () => {
    setLoading(true);
    setError(null);

    console.log('[BoardingInfoModal] Fetching boarding info for waypoint:', waypointId, 'entityId:', entityId);

    try {
      const response = await axios.get(`/api/waypoints/${entityId}/${waypointId}/boarding-info`);
      console.log('[BoardingInfoModal] API response:', response.data);

      if (response.data.success && response.data.data?.boardingInfo) {
        const normalizedInfo = normalizeBoardingInfo(response.data.data.boardingInfo);
        console.log('[BoardingInfoModal] Normalized boarding info:', normalizedInfo);
        setBoardingInfo(normalizedInfo);
        setError(null);
      } else if (!initialInfo) {
        console.warn('[BoardingInfoModal] No boarding info in response');
        setError(t('failedToLoadBoardingInfo'));
      }
    } catch (err) {
      console.error('[BoardingInfoModal] Error fetching boarding info:', err);
      if (!initialInfo) {
        setError(t('failedToLoadBoardingInfo'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMap = () => {
    if (boardingInfo?.url) {
      window.open(boardingInfo.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onClose}>
      <div
        className={cn(
          "relative w-full max-w-lg rounded-lg shadow-xl",
          background.secondary,
          "max-h-[90vh] overflow-y-auto"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between p-4 border-b", border.default)}>
          <div className="flex items-center gap-2">
            <MapPin className={cn("w-5 h-5", text.link)} />
            <h2 className={cn("text-lg font-semibold", text.primary)}>
              {t('boardingInfoTitle')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={cn(
              "p-1 rounded-full transition-colors",
              "hover:bg-gray-100 dark:hover:bg-gray-700",
              text.secondary
            )}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Waypoint Name */}
          <div className="mb-4">
            <p className={cn("text-sm", text.muted)}>{t('stop')}:</p>
            <p className={cn("font-medium", text.primary)}>{waypointName}</p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className={cn(
              "flex items-start gap-2 p-3 rounded",
              "bg-red-50 dark:bg-red-900/20",
              "text-red-600 dark:text-red-400",
              "border border-red-200 dark:border-red-800"
            )}>
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Boarding Info */}
          {!loading && !error && boardingInfo && (
            <>
              {/* No Info Available */}
              {!boardingInfo.hasInfo && (
                <div className={cn(
                  "flex items-start gap-2 p-3 rounded",
                  "bg-gray-50 dark:bg-gray-800",
                  text.secondary,
                  "border", border.default
                )}>
                  <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{t('noBoardingInfo')}</p>
                </div>
              )}

              {/* Text Instructions */}
              {boardingInfo.text && (
                <div className="mb-4">
                  <h3 className={cn("text-sm font-medium mb-2", text.primary)}>
                    {t('instructions')}:
                  </h3>
                  <div className={cn(
                    "p-3 rounded whitespace-pre-wrap",
                    "bg-blue-50 dark:bg-blue-900/20",
                    "border border-blue-200 dark:border-blue-800",
                    text.primary
                  )}>
                    {boardingInfo.text}
                  </div>
                  {boardingInfo.isTranslated && (
                    <p className={cn("text-xs mt-1", text.muted)}>
                      {t('translatedFrom')} {boardingInfo.language}
                    </p>
                  )}
                </div>
              )}

              {/* Map/Guide Link */}
              {boardingInfo.url && (
                <div className="mb-4">
                  <button
                    onClick={handleOpenMap}
                    className={cn(
                      "flex items-center gap-2 w-full p-3 rounded transition-colors",
                      "bg-green-50 dark:bg-green-900/20",
                      "border border-green-200 dark:border-green-800",
                      "hover:bg-green-100 dark:hover:bg-green-900/30",
                      "text-green-700 dark:text-green-300"
                    )}
                  >
                    <ExternalLink className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{t('viewOnMap')}</span>
                  </button>
                </div>
              )}

              {/* Image Preview */}
              {boardingInfo.imageUrl && (
                <div className="mb-4">
                  <h3 className={cn("text-sm font-medium mb-2", text.primary)}>
                    {t('visualGuide')}:
                  </h3>
                  <div className={cn("rounded border", border.default, "overflow-hidden")}>
                    <AppImage
                      src={boardingInfo.imageUrl}
                      alt={t('boardingLocationImage')}
                      crossOrigin="anonymous"
                      fallbackSrc={null}
                      className="w-full h-auto max-h-64 object-contain"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={cn("flex justify-end p-4 border-t", border.default)}>
          <button
            onClick={onClose}
            className={cn(
              "px-4 py-2 rounded transition-colors",
              "bg-blue-500 hover:bg-blue-600",
              "text-white font-medium"
            )}
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoardingInfoModal;
